<!-- 講座画面、メンバー一覧選択時のページ -->
<script setup lang="ts">
  import { useDisplay } from "vuetify";
  import MoleculesDeliveryStepDeliveryCreateDialog from "../Molecules/Delivery/StepDeliveryCreateDialog.vue";
  import { PlanInfo } from "~/entities/PlanInfo";
  import { CONTENT_PAGE_SIZE } from "~/consts/contentsConfig";
  import { useSnackBarStore } from "~/stores/SnackBarStore";
  import { useDeliveryStore } from "~/stores/DeliveryStore";
  import { emptyDelivery, type Delivery } from "~/entities/Delivery";
  import { useHistoryStore } from "~/stores/HistoryStore";

  const { $confirmDialog } = useNuxtApp();

  interface Props {
    planInfo: PlanInfo;
  }
  const localProps = withDefaults(defineProps<Props>(), {});

  defineEmits(["onBack"]);

  const deliveryStore = useDeliveryStore();
  const snackBarStore = useSnackBarStore();
  const historyStore = useHistoryStore();

  const page = ref(1);
  const pageSize = ref(CONTENT_PAGE_SIZE);

  const stepDeliveryCreateDialogRef = ref<InstanceType<typeof MoleculesDeliveryStepDeliveryCreateDialog>>();

  const sort = ref<"desc" | "asc">("desc");
  const search = ref("");

  // レスポンシブ対応
  const { name } = useDisplay();
  const isSp = computed<boolean>((): boolean => name.value === "xs");

  // トランジションエフェクトの関係でデータ取得は外部から行う
  const fetch = () => {
    const options = {
      sort: sort.value,
      page_size: pageSize.value,
      page: page.value,
    };
    deliveryStore.getStepDeliveries({ ...options });
    // ページが変わったらスクロール位置をトップに戻す
    window.scrollTo({
      top: 0,
      behavior: "instant",
    });
  };

  const loading = computed<boolean>(() => deliveryStore.loading);

  const computedPage = computed<number>({
    get: () => page.value,
    set: value => {
      page.value = value;
      fetch();
    },
  });

  const showPagination = computed<boolean>(() => {
    if (deliveryStore.stepDeliveryResponse?.totalPages) {
      return deliveryStore.stepDeliveryResponse.totalPages > 1;
    }
    return false;
  });

  watch(
    () => search.value,
    () => {
      page.value = 1;
      fetch();
    },
  );

  const handleOpenCreateDialog = () => {
    stepDeliveryCreateDialogRef.value?.openModal();
  };

  const handleSave = (val: string) => {
    const delivery = emptyDelivery();
    delivery.name = val;
    deliveryStore.selectedDelivery = delivery;
    historyStore.to(`/plan/${localProps.planInfo.id}/step-delivery/create`);
  };

  const handleAction = (action: { text: string; value: string }, delivery: Delivery) => {
    if (action.value === "edit-item") {
      deliveryStore.selectedDelivery = delivery;
      historyStore.to(`/plan/${localProps.planInfo.id}/step-delivery/${delivery.messageDeliveryid}/edit`);
    } else if (action.value === "copy-item") {
      deliveryStore.selectedDelivery = delivery;
      historyStore.to(`/plan/${localProps.planInfo.id}/step-delivery/${delivery.messageDeliveryid}/edit?type=copy`);
    } else if (action.value === "status-item") {
      onStatus(delivery);
    } else if (action.value === "delete-item") {
      $confirmDialog.openDialog({
        message: `${delivery.name}を削除しますか？`,
        onConfirm: () => onDelete(delivery),
      });
    } else if (action.value === "view-item") {
      deliveryStore.selectedDelivery = delivery;
      historyStore.to(`/plan/${localProps.planInfo.id}/step-delivery/${delivery.messageDeliveryid}/view`);
    }
  };

  const onStatus = async (delivery: Delivery) => {
    try {
      await deliveryStore.changeDeliveryStatus(
        delivery.messageDeliveryid!,
        delivery.status === "RUNNING" ? "STOP" : "RUNNING",
      );
      if (delivery.status === "RUNNING") {
        snackBarStore.set(`${delivery.name}を停止しました`);
      } else {
        snackBarStore.set(`${delivery.name}を配信準備しています`);
      }
      await fetch();
    } catch (e) {
      snackBarStore.setError("一斉配信の停止に失敗しました");
      console.error(e);
    }
  };

  const onDelete = async (delivery: Delivery) => {
    try {
      await deliveryStore.deleteDelivery(delivery.messageDeliveryid!);
      snackBarStore.set(`${delivery.name}を削除しました`);
      await fetch();
    } catch (e) {
      snackBarStore.setError("一斉配信の削除に失敗しました");
      console.error(e);
    }
  };

  onMounted(() => {
    fetch();
  });

  defineExpose({
    fetch,
    loading,
  });
</script>
<template>
  <MoleculesDeliveryStepDeliveryCreateDialog ref="stepDeliveryCreateDialogRef" @on-submit="handleSave" />
  <MoleculesContainerMain
    v-model:search="search"
    title="ステップ配信"
    svg-icon="assets/images/icon/step-delivery.webp"
    @on-back="$emit('onBack')"
  >
    <template #header-btns>
      <v-btn class="add-campaign" @click="handleOpenCreateDialog">
        <v-icon icon="$mdiPlus" color="white" size="large" />
        <span v-if="isSp" class="text-white">作成</span>
        <span v-if="!isSp" class="text-white">新規ステップ配信作成</span>
      </v-btn>
    </template>
    <div class="mx-2 mt-2">
      <template v-if="loading">
        <AtomsDataTableStepDeliveryCardLoading v-for="index in CONTENT_PAGE_SIZE" :key="index" />
      </template>
      <template v-else>
        <div>
          <MoleculesDeliveryPlanStepDeliveryCard
            v-for="(item, index) in deliveryStore.stepDeliveryResponse?.deliveryMessages || []"
            :key="index"
            :delivery="item"
            @on-action="val => handleAction(val, item)"
          />
        </div>
      </template>

      <v-sheet color="transparent" class="pb-4">
        <AtomsPagination
          v-if="showPagination"
          v-model:page="computedPage"
          :total-pages="deliveryStore.stepDeliveryResponse?.totalPages || 0"
        />
      </v-sheet>
    </div>
  </MoleculesContainerMain>
</template>
